import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Coins, Gamepad2, Layers, MapPin, Stamp, Target, Theater } from "lucide-react";
import { api } from "../lib/api";
import { IMAGES } from "../lib/tokens";
import Plate from "../components/Plate";

const KIND = {
  aim: { icon: Target, label: "Aim", color: "var(--arcade-cyan)" },
  ante: { icon: Coins, label: "Ante", color: "var(--stage-gold)" },
  seam: { icon: MapPin, label: "The Seam", color: "#c3a8f0" },
  shard: { icon: Layers, label: "Shards", color: "#e0a44a" },
  cluster: { icon: Layers, label: "Clustered", color: "var(--stage-gold)" },
  mission: { icon: Target, label: "Mission", color: "var(--stage-gold)" },
  mark: { icon: Stamp, label: "Mark", color: "#6ee7b7" },
  ledger: { icon: Coins, label: "Ledger", color: "#ffffff77" },
};

const REALM = {
  playforward: { icon: Gamepad2, label: "PlayForward" },
  stageward: { icon: Theater, label: "Stageward" },
  atlas: { icon: MapPin, label: "The Atlas" },
};

export default function Journal() {
  const nav = useNavigate();
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    api.get("/journal").then(({ data }) => setEntries(data));
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden">
      <Plate src={IMAGES.atlasIslands} depth={6} />

      <div className="relative z-10 mx-auto max-w-3xl px-6 pb-28 pt-20 md:px-10">
        <button
          type="button"
          data-testid="journal-back"
          onClick={() => nav(-1)}
          className="tracked mb-12 flex items-center gap-2 text-[9px] text-white/40 transition-colors duration-200 hover:text-[var(--stage-gold)]"
        >
          <ArrowLeft size={12} /> Back
        </button>

        <div className="slab-cool mb-10 p-7 md:p-9">
        <div className="tracked mb-5 text-[10px] text-[var(--atlas-gold)]/70">
          The Journal · one thread through both realms
        </div>
        <h1 className="serif text-4xl font-light leading-tight text-[#f4ecdd] sm:text-5xl">
          Nothing here is filed by
          <span className="italic text-[var(--atlas-gold)]"> app</span>.
        </h1>
        <p className="mt-6 max-w-xl text-sm leading-relaxed text-white/50">
          PlayForward and Stageward write to the same Journal because they are the same
          World. Read down the column and you can see the moment you crossed.
        </p>
        </div>

        <div className="mt-14" data-testid="journal-list">
          {entries?.length === 0 && (
            <div className="mono text-[11px] text-white/30">
              Nothing written yet. Cross a door.
            </div>
          )}
          {entries?.map((e, i) => {
            const k = KIND[e.kind] || KIND.ledger;
            const r = REALM[e.realm] || REALM.atlas;
            const Icon = k.icon;
            const RealmIcon = r.icon;
            return (
              <motion.div
                key={e.id}
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: Math.min(i, 8) * 0.05 }}
                className="relative border-l border-white/10 pb-8 pl-8 last:pb-0"
                data-testid="journal-entry"
              >
                <div
                  className="absolute -left-[7px] top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full"
                  style={{ background: "#05070c", border: `1px solid ${k.color}` }}
                >
                  <div className="h-1 w-1 rounded-full" style={{ background: k.color }} />
                </div>
                <div className="mono mb-2 flex flex-wrap items-center gap-3 text-[9px]">
                  <span className="tracked" style={{ color: k.color }}>
                    {k.label}
                  </span>
                  <span className="flex items-center gap-1.5 text-white/25">
                    <RealmIcon size={9} strokeWidth={1.6} /> {r.label}
                  </span>
                  <span className="text-white/20">{e.created_at?.slice(0, 19).replace("T", " ")}</span>
                </div>
                <div className="serif text-[15px] leading-relaxed text-[#eee6d8]">{e.text}</div>
                {e.meta?.tx_id && (
                  <div className="mono mt-2 break-all text-[9px] text-white/20">{e.meta.tx_id}</div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
